import { createTheme } from '@abyss/web/tools/theme';
import { OverlayProvider } from '@abyss/web/ui/OverlayProvider';
import { ThemeProvider } from '@abyss/web/ui/ThemeProvider';
import React, { useContext, useEffect } from 'react';

import { SmartChoiceWidget } from '../../common/Widget/SmartChoiceWidget';
import {
  PortalContext,
  PortalContextProvider,
} from '../../context/PortalContext';
import { StoreKeys } from '../../hooks/useStore/state';
import { useStore } from '../../hooks/useStore/useStore';
import { shouldUseGehaV2Styling } from '../../utils/gehaThemeUtils';
import { coerceBoolean } from '../../utils/string.utils';
import { Configuration } from '../ProviderSearch/context/Configuration';
import { DateTimeConfig } from '../ProviderSearch/context/DateTimeConfig';
import { Internationalization } from '../ProviderSearch/context/Internationalization';
import { Obapi } from '../ProviderSearch/context/Obapi';
import { getTheme } from '../themeOverrides/theme.utils';

/**
 * Props for SmartChoiceWidgetApp - uses kebab-case for HTML attribute compatibility
 */
interface SmartChoiceWidgetAppProps {
  breadcrumbs?: any[];
  'flex-css'?: string;
  'is-full-screen-compare-smart-choice'?: boolean | string;
  'is-mixed-results-page'?: boolean | string;
  'is-super-user'?: boolean | string;
  'member-id'?: string;
  'portal-data'?: string;
  'show-choose-pcp-header'?: boolean | string;
  'show-member-selection'?: boolean | string;
  'submit-button-label'?: string;
}

export const SmartChoiceWidgetApp = (args: SmartChoiceWidgetAppProps = {}) => {
  const {
    breadcrumbs = [],
    'flex-css': flexCssJson,
    'is-full-screen-compare-smart-choice': isFullScreenCompareSmartChoice,
    'is-mixed-results-page': isMixedResultsPage,
    'is-super-user': isSuperUserRaw,
    'member-id': memberId,
    'portal-data': portalDataJson,
    'show-choose-pcp-header': showChoosePCPHeader,
    'show-member-selection': showMemberSelection,
    'submit-button-label': submitButtonLabel,
  } = args || {};

  // Convert HTML attributes to proper booleans
  const isSuperUser = coerceBoolean(isSuperUserRaw);
  const isFullScreen = coerceBoolean(isFullScreenCompareSmartChoice);
  const isMixedResults = coerceBoolean(isMixedResultsPage);
  const shouldShowChoosePCPHeader = coerceBoolean(showChoosePCPHeader);
  const shouldShowMemberSelection = coerceBoolean(showMemberSelection, true);

  const setIsWidget = useStore(StoreKeys.SET_IS_WIDGET);

  const portalData = JSON.parse(portalDataJson || '{}');
  const flexCss = flexCssJson ? JSON.parse(flexCssJson) : undefined;
  const { setPortalData } = useContext(PortalContext);
  const isPortalWidget = !!portalData?.portalName;
  const isGEHAV2: boolean = shouldUseGehaV2Styling();

  const themeOverride = getTheme(
    portalData?.portalConfig?.theme,
    portalData?.portalIdentifier,
    portalData?.portalConfig?.themeOverride,
    isGEHAV2
  );

  const theme = createTheme(
    portalData?.portalConfig?.theme || 'uhc',
    themeOverride
  );

  useEffect(() => {
    setIsWidget(true);
  }, []);

  useEffect(() => {
    if (isPortalWidget) {
      setPortalData(portalData);
    }
  }, [isPortalWidget, portalData, setPortalData]);

  if (isPortalWidget) {
    return (
      <PortalContextProvider portalDataArgs={portalData}>
        <Configuration isWidget>
          <DateTimeConfig>
            <Obapi isWidget>
              <Internationalization>
                <OverlayProvider>
                  <ThemeProvider theme={theme}>
                    <SmartChoiceWidget
                      breadcrumbs={breadcrumbs}
                      flexCss={flexCss}
                      isFullScreenCompareSmartChoice={isFullScreen}
                      isMixedResultsPage={isMixedResults}
                      isParcelWidget={true}
                      isSuperUser={isSuperUser}
                      memberId={memberId}
                      showChoosePCPHeader={shouldShowChoosePCPHeader}
                      showMemberSelection={shouldShowMemberSelection}
                      submitButtonLabel={submitButtonLabel}
                    />
                  </ThemeProvider>
                </OverlayProvider>
              </Internationalization>
            </Obapi>
          </DateTimeConfig>
        </Configuration>
      </PortalContextProvider>
    );
  }

  return null;
};
